<template>
  <div class="doc-upload-component">
    <v-file-input
      ref="fileinput"
      :placeholder="$t('customFields.forms.elements.multiDocUpload.placeholders.uploadDocuments')"
      :required="required"
      :rules="rules"
      :model-value="computedValue"
      validate-on="submit"
      single-line
      hide-input
      multiple
      hide-details="auto"
      prepend-icon=""
      @update:model-value="onFilesChanged"></v-file-input>
    <outline-button
      v-if="!fileKeys?.length"
      :loading="isSelectingFiles"
      @click="fileUploadClick"
      before-icon="cloud-upload">
      {{ $t('customFields.forms.elements.multiDocUpload.buttons.uploadFiles') }}
      <span v-if="required" class="text-red ml-1"><strong>*</strong></span>
    </outline-button>
    <div v-else class="multi-doc-container">
      <v-btn
        height="auto"
        variant="tonal"
        :loading="isSelectingFiles"
        @click="fileUploadClick"
        class="multi-doc-upload-button"
        v-for="(fileKey, i) in fileKeys"
        :key="fileKey">
        <v-img
          v-if="isImageFile(fileKey)"
          cover
          height="100"
          width="100"
          :src="fileUrls[i]"></v-img>
        <v-icon v-else size="100" color="secondary">mdi-file</v-icon>
        <v-btn
          icon
          density="comfortable"
          size="x-small"
          class="doc-close-button"
          :class="{ 'is-image': isImageFile(fileKey) }"
          @click.stop="deleteDoc(fileKey)">
          <v-icon size="x-small">mdi-close</v-icon>
        </v-btn>
        <div class="font-italic text-subtitle-2">{{ getFileName(fileKey) }}</div>
      </v-btn>
    </div>
    <div v-if="shouldShowError" class="v-messages mt-2" role="alert">
      <div class="v-messages__message text-error">{{ fieldError }}</div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    /**
     * v-model
     */
    value: {
      type: Array,
      required: false,
      default: () => []
    },
    /**
     * Field is required
     */
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Validation rules
     */
    rules: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  computed: {
    /**
     * Get the first error from bucket if exists
     * @returns {string|boolean}
     */
    fieldError() {
      if (!this.mounted || !this.$refs.fileinput) {
        return false;
      }

      const errorMessages = this.$refs.fileinput.errorMessages;
      return errorMessages?.length ? errorMessages[0] : false;
    },
    /**
     * Determine if error should be shown
     * Only show after validation has been attempted
     * @returns {boolean}
     */
    shouldShowError() {
      if (!this.mounted || !this.$refs.fileinput || !this.fieldError) {
        return false;
      }

      // Check if the input has been validated (isValid will be false if validation failed)
      const hasBeenValidated = this.$refs.fileinput.isValid === false;

      return hasBeenValidated && this.fieldError;
    },
    computedValue() {
      // This sucks, but it is a workaround for us having zero control over the vuetify prop definition.
      // The Vuetify component expects a list of Files as a value. We don't have files, we have strings (remote
      // file keys). So what? We need to create dummy files so that the Form Validation knows there is something,
      // as the value can't be the string array. Let's consider moving away from Vuetify v-file-input and create our
      // own component to get rid of this.
      return this.fileKeys?.length
        ? this.fileKeys.map(() => new File([], '__vuetify_placeholder__'))
        : [];
    }
  },
  data() {
    return {
      mounted: false,
      isSelectingFiles: false,
      fileUrls: [],
      fileKeys: [],
      selectedFiles: []
    };
  },

  methods: {
    getFileName(fileKey) {
      return this.novaCore.getDocumentNameFromUrl(fileKey);
    },
    /**
     * Determines if file is image for thumbnail display purposes
     * @returns {*|boolean}
     */
    isImageFile(fileKey) {
      return this.novaCore.isImageUrl(fileKey);
    },
    /**
     * Handles file upload button click
     * @public
     */
    fileUploadClick() {
      window.addEventListener(
        'focus',
        () => {
          this.isSelectingFiles = false;
        },
        { once: true }
      );

      this.$refs.fileinput.$el.querySelector('input').click();
    },
    /**
     * Handles file selection change
     * @public
     * @returns {Promise<void>}
     */
    async onFilesChanged(newFiles) {
      // Filter out our placeholder files
      const realFiles = newFiles.filter(
        file => file.name !== '__vuetify_placeholder__' && file.size > 0
      );

      if (realFiles.length === 0) {
        return;
      }

      const remainingSlots =
        this.novaCore.MULTI_DOC_MAX_FILES_PER_APPOINTMENT - this.fileKeys.length;

      if (realFiles.length > remainingSlots) {
        this.notify(
          this.$t('customFields.forms.elements.multiDocUpload.messages.maximumOfFilesAllowed', {
            maxFiles: this.novaCore.MULTI_DOC_MAX_FILES_PER_APPOINTMENT
          }),
          'error'
        );

        return;
      }

      this.$emit('uploading');
      this.isSelectingFiles = true;

      try {
        const formData = new FormData();
        realFiles.forEach(file => {
          formData.append('files', file);
        });

        const response = await axios.post('/storage/files', formData);

        // Response is an array of uploaded file objects
        response.data.forEach(fileData => {
          this.fileUrls.push(fileData.url);
          this.fileKeys.push(fileData.key);
        });

        // Reset validation state when files are added
        if (this.$refs.fileinput) {
          this.$nextTick(() => {
            this.$refs.fileinput.resetValidation();
          });
        }

        this.$emit('update:model-value', this.fileKeys);
        this.$emit('uploaded');
      } catch (error) {
        this.notify(
          this.$t('customFields.forms.elements.multiDocUpload.messages.uploadFailed'),
          'error'
        );
        console.error('File upload failed:', error);
      } finally {
        this.isSelectingFiles = false;
      }
    },
    deleteDoc(fileKey) {
      this.fileUrls = this.fileUrls.filter(url => !url.endsWith(fileKey));
      this.fileKeys = this.fileKeys.filter(key => key !== fileKey);
      this.$emit('update:model-value', this.fileKeys);
    }
  },
  mounted() {
    this.fileUrls = Array.isArray(this.value) ? this.value : [];

    // Set default value for the v-model
    if (this.fileUrls?.length) {
      this.fileKeys = this.fileUrls.map(value => {
        try {
          return new URL(value).pathname.slice(1);
        } catch {
          return value;
        }
      });
    }

    this.mounted = true;

    this.$emit('update:model-value', this.fileKeys);
  }
};
</script>
